import { LitElement, css } from 'lit';
import { property } from 'lit/decorators.js';

// Common size variants
export type SizeVariant = 'small' | 'medium' | 'large';

// Common color variants
export type ColorVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

/**
 * Base class for all EWC components
 * Provides common properties, theme support, and Tailwind design tokens
 */
export abstract class EwcBase extends LitElement {
  @property({ type: String })
  size: SizeVariant = 'medium';

  @property({ type: String })
  variant: ColorVariant = 'primary';

  @property({ type: Boolean })
  disabled = false;

  static get styles() {
    return css`
      :host {
        display: inline-block;
        box-sizing: border-box;
        font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif;
      }

      :host([disabled]) {
        pointer-events: none;
        opacity: 0.5;
        cursor: not-allowed;
      }

      /* Tailwind-inspired design tokens */
      :host {
        /* Spacing scale */
        --ewc-space-1: 0.25rem;
        --ewc-space-2: 0.5rem;
        --ewc-space-3: 0.75rem;
        --ewc-space-4: 1rem;
        --ewc-space-5: 1.25rem;
        --ewc-space-6: 1.5rem;
        --ewc-space-8: 2rem;
        
        /* Border radius */
        --ewc-radius-sm: 0.25rem;
        --ewc-radius: 0.375rem;
        --ewc-radius-md: 0.5rem;
        --ewc-radius-lg: 0.75rem;
        --ewc-radius-xl: 1rem;
        --ewc-radius-full: 9999px;
        
        /* Shadows */
        --ewc-shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
        --ewc-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
        --ewc-shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
        --ewc-shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
        --ewc-shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
        
        /* Transitions */
        --ewc-transition-fast: 150ms cubic-bezier(0.4, 0, 0.2, 1);
        --ewc-transition: 200ms cubic-bezier(0.4, 0, 0.2, 1);
        --ewc-transition-slow: 300ms cubic-bezier(0.4, 0, 0.2, 1);
        
        /* Z-index scale */
        --ewc-z-dropdown: 1000;
        --ewc-z-sticky: 1020;
        --ewc-z-fixed: 1030;
        --ewc-z-modal-backdrop: 1040;
        --ewc-z-modal: 1050;
        --ewc-z-popover: 1060;
        --ewc-z-tooltip: 1070;
      }

      /* Size variants */
      :host([size="small"]) {
        --ewc-font-size: 0.875rem;
        --ewc-line-height: 1.25rem;
        --ewc-padding-x: 0.75rem;
        --ewc-padding-y: 0.375rem;
        --ewc-height: 2rem;
      }

      :host([size="medium"]) {
        --ewc-font-size: 0.875rem;
        --ewc-line-height: 1.25rem;
        --ewc-padding-x: 1rem;
        --ewc-padding-y: 0.5rem;
        --ewc-height: 2.5rem;
      }

      :host([size="large"]) {
        --ewc-font-size: 1rem;
        --ewc-line-height: 1.5rem;
        --ewc-padding-x: 1.25rem;
        --ewc-padding-y: 0.625rem;
        --ewc-height: 3rem;
      }

      /* Color variants */
      :host([variant="primary"]) {
        --ewc-color-50: #eff6ff;
        --ewc-color-100: #dbeafe;
        --ewc-color-200: #bfdbfe;
        --ewc-color-300: #93c5fd;
        --ewc-color-400: #60a5fa;
        --ewc-color-500: #3b82f6;
        --ewc-color-600: #2563eb;
        --ewc-color-700: #1d4ed8;
        --ewc-color-800: #1e40af;
        --ewc-color-900: #1e3a8a;
        --ewc-color: var(--ewc-color-600);
        --ewc-color-hover: var(--ewc-color-700);
        --ewc-color-active: var(--ewc-color-800);
        --ewc-text-color: #ffffff;
      }

      :host([variant="secondary"]) {
        --ewc-color-50: #f9fafb;
        --ewc-color-100: #f3f4f6;
        --ewc-color-200: #e5e7eb;
        --ewc-color-300: #d1d5db;
        --ewc-color-400: #9ca3af;
        --ewc-color-500: #6b7280;
        --ewc-color-600: #4b5563;
        --ewc-color-700: #374151;
        --ewc-color-800: #1f2937;
        --ewc-color-900: #111827;
        --ewc-color: var(--ewc-color-600);
        --ewc-color-hover: var(--ewc-color-700);
        --ewc-color-active: var(--ewc-color-800);
        --ewc-text-color: #ffffff;
      }

      :host([variant="success"]) {
        --ewc-color-50: #f0fdf4;
        --ewc-color-100: #dcfce7;
        --ewc-color-200: #bbf7d0;
        --ewc-color-300: #86efac;
        --ewc-color-400: #4ade80;
        --ewc-color-500: #22c55e;
        --ewc-color-600: #16a34a;
        --ewc-color-700: #15803d;
        --ewc-color-800: #166534;
        --ewc-color-900: #14532d;
        --ewc-color: var(--ewc-color-600);
        --ewc-color-hover: var(--ewc-color-700);
        --ewc-color-active: var(--ewc-color-800);
        --ewc-text-color: #ffffff;
      }

      :host([variant="warning"]) {
        --ewc-color-50: #fffbeb;
        --ewc-color-100: #fef3c7;
        --ewc-color-200: #fde68a;
        --ewc-color-300: #fcd34d;
        --ewc-color-400: #fbbf24;
        --ewc-color-500: #f59e0b;
        --ewc-color-600: #d97706;
        --ewc-color-700: #b45309;
        --ewc-color-800: #92400e;
        --ewc-color-900: #78350f;
        --ewc-color: var(--ewc-color-600);
        --ewc-color-hover: var(--ewc-color-700);
        --ewc-color-active: var(--ewc-color-800);
        --ewc-text-color: #ffffff;
      }

      :host([variant="error"]) {
        --ewc-color-50: #fef2f2;
        --ewc-color-100: #fee2e2;
        --ewc-color-200: #fecaca;
        --ewc-color-300: #fca5a5;
        --ewc-color-400: #f87171;
        --ewc-color-500: #ef4444;
        --ewc-color-600: #dc2626;
        --ewc-color-700: #b91c1c;
        --ewc-color-800: #991b1b;
        --ewc-color-900: #7f1d1d;
        --ewc-color: var(--ewc-color-600);
        --ewc-color-hover: var(--ewc-color-700);
        --ewc-color-active: var(--ewc-color-800);
        --ewc-text-color: #ffffff;
      }

      :host([variant="info"]) {
        --ewc-color-50: #ecfeff;
        --ewc-color-100: #cffafe;
        --ewc-color-200: #a5f3fc;
        --ewc-color-300: #67e8f9;
        --ewc-color-400: #22d3ee;
        --ewc-color-500: #06b6d4;
        --ewc-color-600: #0891b2;
        --ewc-color-700: #0e7490;
        --ewc-color-800: #155e75;
        --ewc-color-900: #164e63;
        --ewc-color: var(--ewc-color-600);
        --ewc-color-hover: var(--ewc-color-700);
        --ewc-color-active: var(--ewc-color-800);
        --ewc-text-color: #ffffff;
      }

      /* Light theme colors */
      :host {
        --ewc-bg-color: #ffffff;
        --ewc-bg-secondary: #f9fafb;
        --ewc-bg-tertiary: #f3f4f6;
        --ewc-border-color: #e5e7eb;
        --ewc-text-color-default: #111827;
        --ewc-text-color-secondary: #6b7280;
        --ewc-text-color-tertiary: #9ca3af;
        --ewc-hover-bg: #f9fafb;
        --ewc-ring-color: var(--ewc-color-500);
      }

      /* Dark theme adjustments */
      :host([theme="dark"]) {
        --ewc-bg-color: #0f172a;
        --ewc-bg-secondary: #1e293b;
        --ewc-bg-tertiary: #334155;
        --ewc-border-color: #334155;
        --ewc-text-color-default: #f8fafc;
        --ewc-text-color-secondary: #cbd5e1;
        --ewc-text-color-tertiary: #94a3b8;
        --ewc-hover-bg: #1e293b;
        
        /* Shadows for dark mode */
        --ewc-shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.25);
        --ewc-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.3), 0 1px 2px -1px rgb(0 0 0 / 0.3);
        --ewc-shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.3), 0 2px 4px -2px rgb(0 0 0 / 0.3);
        --ewc-shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.3), 0 4px 6px -4px rgb(0 0 0 / 0.3);
        --ewc-shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.3), 0 8px 10px -6px rgb(0 0 0 / 0.3);
      }

      /* Focus styles */
      :focus {
        outline: 2px solid transparent;
        outline-offset: 2px;
      }
      
      :focus-visible {
        --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
        --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--ewc-ring-color);
        box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
      }

      /* Transition defaults */
      * {
        transition-property: color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: var(--ewc-transition-fast);
      }
    `;
  }

  connectedCallback() {
    super.connectedCallback();
    // Check for theme preference
    this._updateTheme();
    // Listen for theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => this._updateTheme());
  }

  private _updateTheme() {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const theme = this.getAttribute('theme') || (prefersDark ? 'dark' : 'light');
    this.setAttribute('theme', theme);
  }
}